import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Button, Spinner, Alert, Badge, Switch, Modal } from '@components/ui';
import {
  PlusIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  UserIcon,
  IdentificationIcon,
  InformationCircleIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { useInvoices } from '../../invoices/hooks/useInvoices';
import { useSelectedCompany } from '../../company/hooks/useSelectedCompany';
import { useInvoiceFilterStore, filterErrorInvoices } from '@/stores/invoiceFilterStore';
import {
  StatusChip,
  DocTypeBadge,
  InvoiceRowsSkeleton,
  CARD,
  FIELD,
  FIELD_LABEL,
  CTA,
  GHOST,
  TH,
  TD,
  FieldInfo,
} from './InvoiceTableAtoms';
import { ROW_HOVER, INVOICE_STATUS_GROUPS } from '@components/ui/designTokens';

/**
 * Facturas emitidas — Claude Design · FacturasEmitidas.dc.html
 *
 * El diseño reagrupa las 11 columnas de la tabla en 7, pero no pierde ningun
 * dato: Documento junta numero + tipo + CUFE + resolucion, Cliente junta
 * nombre + RUC, y Tienda/Punto se muestran juntos. Todos los campos que
 * mostraba la version anterior siguen visibles.
 *
 * Sin resumen/franja de conteos arriba de los filtros (se probo con una
 * franja de "En esta página" + barra de composicion por estado y se sacó a
 * pedido) — la pantalla arranca directo en Filtros → Resultados.
 */

// Panama usa el Balboa, igual que en el dashboard.
const CURRENCY = 'B/.';

/**
 * Los 4 estados reales del dominio, con sus hex — ahora viven en
 * designTokens.ts (`INVOICE_STATUS_GROUPS`) para que esta pantalla, la tabla
 * (`StatusChip` en InvoiceTableAtoms) y cualquier otra que los necesite lean
 * siempre el mismo color por estado. Antes estaban duplicados a mano aca Y en
 * InvoiceTableAtoms con valores que no coincidían del todo — de ahí salía
 * "los colores no salen bien" en la columna Estado de la tabla.
 */
const STATE_GROUPS = INVOICE_STATUS_GROUPS;

/**
 * Opciones del chip de filtro "Estado", construidas desde STATE_GROUPS en vez
 * de repetir los 4 estados a mano — antes este filtro mostraba los 6
 * eInvoiceStatus granulares de Aludra sueltos (p.ej. "Procesada" Y
 * "Procesando" como chips separados), que no son los 4 estados reales del
 * negocio y se leían como un error ("¿por qué hay dos parecidos?").
 * Los chips permiten elegir mas de uno a la vez (ver invoiceStatusKeys en
 * MainPage) — `key` identifica el grupo elegido; los codigos reales
 * (`codes`) se combinan recien al armar el string que se manda al backend.
 * (invoice.controller.ts) ya interpreta un set de codigos separados por
 * coma, no un unico eInvoiceStatus — es lo mismo que usaba "Autorizado uso
 * de la FE" con "2,3,4", solo que ahora la combinacion puede venir de mas
 * de un chip.
 */
const ESTADO_FILTER_OPTIONS: { key: string; label: string; codes: number[]; dot: string | null }[] = [
  { key: '', label: 'Todos', codes: [], dot: null },
  ...STATE_GROUPS.map((g) => ({ key: g.key, label: g.label, codes: g.codes, dot: g.bar })),
];

// Aludra almacena el CUFE sin el prefijo "FE"; se agrega aquí solo para mostrarlo.
const formatCufe = (cufe?: string): string => {
  if (!cufe) return '-';
  return cufe.startsWith('FE') ? cufe : `FE${cufe}`;
};

// Tag de tipo de documento junto al N° de documento (replica getTagByDoctType
// de PortalEInvoice — Views\EmiInvoice\_InvoiceTable.cshtml:31-89)
const docTypeTags: Record<string, { text: string; variant: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info'; title: string }> = {
  '01': { text: 'FOI', variant: 'default', title: 'Factura de Operación Interna' },
  '02': { text: 'FI', variant: 'primary', title: 'Factura de Importación' },
  '03': { text: 'FEXP', variant: 'info', title: 'Factura de Exportación' },
  '04': { text: 'NC', variant: 'success', title: 'Nota de Crédito Referente a una o Varias FE' },
  '05': { text: 'ND', variant: 'warning', title: 'Nota de Débito Referente a una o Varias FE' },
  '06': { text: 'NCG', variant: 'primary', title: 'Nota de Crédito Genérica' },
  '07': { text: 'NDG', variant: 'primary', title: 'Nota de Débito Genérica' },
  '08': { text: 'FZF', variant: 'primary', title: 'Factura de Zona Franca' },
  '09': { text: 'REM', variant: 'primary', title: 'Reembolso' },
  '10': { text: 'FOE', variant: 'primary', title: 'Factura de operación extranjera' },
};
const getDocTypeTag = (documentType?: string) =>
  (documentType && docTypeTags[documentType]) || { text: 'FNI', variant: 'success' as const, title: 'Factura no identificada' };

// Mapeo de colores segun EInvoiceStatus (equivalente a los label-* de Bootstrap)
const getStatusVariant = (eInvoiceStatus: number): 'success' | 'danger' | 'warning' | 'default' => {
  switch (eInvoiceStatus) {
    case 0: return 'success';    // Procesado
    case 1: return 'danger';     // Error
    case 2: return 'warning';    // Autorizado uso de FE
    case 3: return 'warning';    // Procesando
    case 4: return 'warning';    // Pendiente
    case 5: return 'default';    // Anulada
    default: return 'default';
  }
};

// Mapeo de variante de Badge a color de texto para el icono de advertencia
const getIconColorClass = (variant: string): string => {
  switch (variant) {
    case 'info': return 'text-primary-600';
    case 'danger': return 'text-danger-600';
    case 'warning': return 'text-warning-600';
    default: return 'text-secondary-600';
  }
};

/**
 * Altura unica de la fila de busqueda. El input la definia por su padding
 * (~44px) y "Mas filtros" estaba clavado en 42px, asi que quedaban
 * desalineados por 2px. Ahora los tres controles salen de aca.
 */
const CONTROL_H = 'h-11';

function StatusBadgeWithModal({
  status,
  eInvoiceStatus,
  responseCode,
  resolution,
  forceWaitOnEndpoints,
  testIdBase,
}: {
  status: string;
  eInvoiceStatus: number;
  responseCode?: string;
  resolution?: string;
  forceWaitOnEndpoints?: string;
  testIdBase?: string;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const variant = getStatusVariant(eInvoiceStatus);
  const tooltipText = [responseCode, resolution].filter(Boolean).join(' - ');
  const showWarningIcon = forceWaitOnEndpoints === 'true';
  const iconColor = getIconColorClass(variant);

  return (
    <>
      <span className="inline-flex items-center gap-1" title={tooltipText || undefined}>
        {showWarningIcon && (
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            data-testid={testIdBase ? `${testIdBase}-warning-button` : undefined}
            className={`${iconColor} hover:opacity-70 transition-opacity`}
            title="Ver detalle del estado"
          >
            <ExclamationCircleIcon className="w-4 h-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          data-testid={testIdBase ? `${testIdBase}-badge-button` : undefined}
        >
          <StatusChip eInvoiceStatus={eInvoiceStatus}>{status || 'Desconocido'}</StatusChip>
        </button>
      </span>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        kicker="Documento · Estado"
        title="Detalle del estado"
        icon={<InformationCircleIcon />}
        size="sm"
        data-testid={testIdBase ? `${testIdBase}-modal` : undefined}
        footer={
          <div className="ml-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsModalOpen(false)}
              data-testid={testIdBase ? `${testIdBase}-modal-close-button` : undefined}
            >
              Cerrar
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Badge variant={variant} size="md">
              {status || 'Desconocido'}
            </Badge>
          </div>
          {responseCode && (
            <div className="rounded-lg bg-secondary-50 dark:bg-secondary-700 p-3">
              <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">
                Código de respuesta
              </p>
              <p className="mt-1 break-words [overflow-wrap:anywhere] text-sm text-secondary-900 dark:text-white">
                {responseCode}
              </p>
            </div>
          )}
          {resolution && (
            <div className="rounded-lg bg-secondary-50 dark:bg-secondary-700 p-3">
              <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">
                Resolución
              </p>
              <p className="mt-1 break-words [overflow-wrap:anywhere] text-sm text-secondary-900 dark:text-white">
                {resolution}
              </p>
            </div>
          )}
          {!responseCode && !resolution && (
            <p className="text-sm text-secondary-500 dark:text-secondary-400">
              No hay información adicional disponible.
            </p>
          )}
        </div>
      </Modal>
    </>
  );
}


export function MainPage() {
  // Calcular el primer día del mes actual
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  };

  // Calcular el día de hoy
  const getToday = () => {
    return new Date().toISOString().split('T')[0];
  };

  const [page, setPage] = useState(1);
  // Cursor de escaneo incremental para busquedas con filtro de texto/nombre/
  // estado (ver invoiceSearchCursor.ts en el backend) — undefined = primera
  // pagina. `cursorHistory` guarda los cursores ya visitados para que
  // "Anterior" pueda volver sin re-escanear nada; vive solo en memoria del
  // componente (no en el servidor), se resetea con cada busqueda nueva.
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const cursorHistoryRef = useRef<(string | undefined)[]>([]);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  // "Mas filtros" es un panel en el flujo normal de la pantalla (ver mas
  // abajo). Este ref cubre la fila de busqueda + el panel para poder
  // cerrarlo al hacer click afuera o con Escape.
  const filterPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showMoreFilters) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(e.target as Node)) {
        setShowMoreFilters(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowMoreFilters(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showMoreFilters]);
  const [cufe, setCufe] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerNumberId, setCustomerNumberId] = useState('');
  // "Estado" permite elegir varios chips a la vez — `invoiceStatusKeys` guarda
  // las claves de los grupos elegidos (STATE_GROUPS: processed/authorized/
  // error/cancelled). `invoiceStatus` deriva de ahi el string de codigos
  // separados por coma que ya entiende el backend (ver ESTADO_FILTER_OPTIONS
  // arriba). Sin chips elegidos ("Todos") sigue siendo '' = sin filtro.
  const [invoiceStatusKeys, setInvoiceStatusKeys] = useState<string[]>([]);
  const invoiceStatus = useMemo(
    () =>
      invoiceStatusKeys.length === 0
        ? ''
        : STATE_GROUPS.filter((g) => invoiceStatusKeys.includes(g.key))
            .flatMap((g) => g.codes)
            .join(','),
    [invoiceStatusKeys]
  );
  const [documentType, setDocumentType] = useState('');
  const [storeCode, setStoreCode] = useState('');
  const [billingPoint, setBillingPoint] = useState('');
  const [initDate, setInitDate] = useState(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState(getToday());

  // Global filter state (persists across views)
  const { hideErrorInvoices, setHideErrorInvoices } = useInvoiceFilterStore();

  // Estados para los filtros aplicados (los que se envían al API)
  const [appliedFilters, setAppliedFilters] = useState({
    cufe: '',
    customerName: '',
    customerNumberId: '',
    invoiceStatus: '',
    documentType: '',
    storeCode: '',
    billingPoint: '',
    initDate: getFirstDayOfMonth(),
    endDate: getToday(),
  });

  /**
   * Aplica los filtros "borrador" (los que el usuario esta tocando) a
   * `appliedFilters` (los que realmente se le mandan al API) y vuelve a la
   * pagina 1 — una busqueda nueva no puede seguir parada en la pagina 4 de
   * la busqueda anterior.
   */
  const applyFilters = () => {
    setAppliedFilters({
      cufe,
      customerName,
      customerNumberId,
      invoiceStatus,
      documentType,
      storeCode,
      billingPoint,
      initDate,
      endDate,
    });
    setPage(1);
    // Una busqueda nueva arranca el escaneo de Aludra desde el principio —
    // sin esto, cambiar de filtro pero seguir mandando un cursor de la
    // busqueda anterior mezclaria resultados de dos busquedas distintas.
    setCursor(undefined);
    cursorHistoryRef.current = [];
  };

  /**
   * Busqueda automatica: cualquier cambio en un filtro la dispara solo, sin
   * depender de que el usuario haga click en "Buscar" — el boton y Enter
   * (`onEnterSearch`) siguen andando para forzarla al instante, cancelando
   * este debounce. El delay evita una consulta por cada tecla en los campos
   * de texto (CUFE/N.º doc, nombre, RUC, tienda, punto de venta); en
   * selects/fechas/chips no se nota porque ya son un solo click, no una
   * tecla a la vez.
   */
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    debounceRef.current = setTimeout(applyFilters, 450);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cufe, customerName, customerNumberId, invoiceStatus, documentType, storeCode, billingPoint, initDate, endDate]);

  // Get selected company
  const { data: selectedCompanyData } = useSelectedCompany();

  // Extract companyId to use in the query key
  const companyId = selectedCompanyData?.company?.id;

  const { data, isLoading, isFetching, error, refetch } = useInvoices({
    page,
    pageSize: 10,
    initDate: appliedFilters.initDate,
    endDate: appliedFilters.endDate,
    // Este campo busca por CUFE O número de documento a la vez (como en la
    // version pre-migracion — "Búsqueda por cufe o número de documento").
    // Va como `search`, no como `cufe`: ese parametro es un filtro EXACTO que
    // Aludra aplica del lado del servidor, asi que si el usuario tipeaba un
    // numero de documento ahi, Aludra lo comparaba contra el campo Cufe y no
    // encontraba nada. `search` recorre documentNumber/cufe/customerName del
    // lado del backend (ver invoice.controller.ts) y por eso encuentra ambos.
    search: appliedFilters.cufe?.trim() || undefined,
    customerName: appliedFilters.customerName || undefined,
    customerNumberId: appliedFilters.customerNumberId || undefined,
    invoiceStatus: appliedFilters.invoiceStatus || undefined,
    documentType: appliedFilters.documentType || undefined,
    storeCode: appliedFilters.storeCode || undefined,
    billingPoint: appliedFilters.billingPoint || undefined,
    companyId, // Include companyId so React Query creates a new cache entry per company
    cursor,
  });

  // Filter invoices to hide those with errors if toggle is on (uses global store)
  const filteredInvoices = filterErrorInvoices(data?.invoices || [], hideErrorInvoices);


  const handleSearch = () => {
    // El backend es el único responsable de hacer el rango de fecha fin inclusivo (T23:59:59 -05:00)
    // Cancela el debounce pendiente: sin esto, click en "Buscar" aplicaba ya
    // mismo Y el timer de la busqueda automatica volvia a aplicar los mismos
    // valores 450ms despues — una consulta de mas, siempre identica.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    applyFilters();
  };

  const handleClearFilters = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const defaultInitDate = getFirstDayOfMonth();
    const defaultEndDate = getToday();
    setCufe('');
    setCustomerName('');
    setCustomerNumberId('');
    setInvoiceStatusKeys([]);
    setDocumentType('');
    setStoreCode('');
    setBillingPoint('');
    setInitDate(defaultInitDate);
    setEndDate(defaultEndDate);
    setAppliedFilters({
      cufe: '',
      customerName: '',
      customerNumberId: '',
      invoiceStatus: '',
      documentType: '',
      storeCode: '',
      billingPoint: '',
      initDate: defaultInitDate,
      endDate: defaultEndDate,
    });
    setPage(1);
    setCursor(undefined);
    cursorHistoryRef.current = [];
  };

  const onEnterSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch();
  };

  /** Hay algo que limpiar: algun campo con valor o un rango de fechas movido. */
  const hasActiveFilters =
    !!(cufe || customerName || customerNumberId || invoiceStatus || documentType || storeCode || billingPoint) ||
    initDate !== getFirstDayOfMonth() ||
    endDate !== getToday();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" data-testid="home-main-loading-spinner" />
      </div>
    );
  }

  if (error) {
    const err = error as any;
    const status = err?.response?.status;
    const message = err?.response?.data?.message || err?.message || 'Error desconocido';
    return (
      <Alert variant="danger" title="Error al cargar facturas">
        <p>
          {status ? `[${status}] ` : ''}
          {message}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-3" data-testid="home-main-retry-button">
          Reintentar
        </Button>
      </Alert>
    );
  }

  // Con filtro de texto/nombre/estado (`data.searchApplied`) no hay total
  // exacto — el backend escanea Aludra de forma incremental y no sabe cuantas
  // coincidencias hay en total sin agotar el dataset (ver InvoiceListResponse).
  // Ahi se pagina con `hasMore`/`nextCursor`, no con "Pagina X de Y".
  const totalPages = data && !data.searchApplied && typeof data.total === 'number'
    ? Math.max(1, Math.ceil(data.total / data.pageSize))
    : 1;

  const handleNextPage = () => {
    if (data?.searchApplied) {
      if (!data.nextCursor) return;
      cursorHistoryRef.current.push(cursor);
      setCursor(data.nextCursor);
    } else {
      setPage((p) => p + 1);
    }
  };

  const handlePrevPage = () => {
    if (data?.searchApplied) {
      const previousCursor = cursorHistoryRef.current.pop();
      setCursor(previousCursor);
    } else {
      setPage((p) => p - 1);
    }
  };

  return (
    <div className="font-display">
      {/* Cabecera */}
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          {/*
            El ultimo segmento del breadcrumb ES el titulo (h1), mas grande —
            no un breadcrumb chico seguido de un h1 que repite el mismo texto
            dos veces. "Inicio" y "/" se quedan al tamaño de breadcrumb;
            "Facturas emitidas" salta al tamaño de titulo en la misma linea.
          */}
          <nav className="flex items-baseline gap-2">
            <span className="text-[14px] text-secondary-400">Inicio</span>
            <span className="text-[14px] text-secondary-300">/</span>
            <h1 className="text-2xl font-bold tracking-[-0.03em] text-ink-850 dark:text-white">
              Facturas emitidas
            </h1>
          </nav>
          <p className="mt-2 text-sm text-secondary-500 dark:text-secondary-400">
            Consulta, filtra y da seguimiento a los documentos emitidos ante la DGI.
          </p>
        </div>
        <Link to="/invoices/new" className={CTA} data-testid="home-main-new-invoice-button">
          <PlusIcon className="h-3.5 w-3.5" />
          Emitir Factura
        </Link>
      </div>

      {/* Filtros */}
      <div className={`${CARD} mt-4 p-5`}>
        {/*
          "Buscar" va pegado al campo, formando un solo control de busqueda.
          Antes era un boton suelto identico al de "Emitir Factura" y al de
          "Aplicar filtros" — tres botones oscuros iguales en la misma
          pantalla, y dos de ellos ejecutaban la misma accion.
        */}
        <div ref={filterPanelRef} className="relative">
        <div className="grid grid-cols-1 items-end gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex items-center gap-1">
              <label htmlFor="cufe" className={FIELD_LABEL}>CUFE / N.º de documento</label>
              <FieldInfo text="Buscá una factura por su código CUFE o por el número de documento — no importa cuál de los dos escribas, este cuadro los reconoce a ambos." />
            </div>
            <div className="flex min-w-0">
              <div className="relative min-w-0 flex-1">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
                <input
                  id="cufe"
                  type="text"
                  placeholder="Buscar por CUFE o número de documento…"
                  value={cufe}
                  onChange={(e) => setCufe(e.target.value)}
                  data-testid="home-main-cufe-input"
                  onKeyDown={onEnterSearch}
                  className={`${FIELD} ${CONTROL_H} rounded-r-none border-r-0 py-0 pl-11 font-mono text-[14px]`}
                />
              </div>
              <button
                type="button"
                onClick={handleSearch}
                data-testid="home-main-search-button"
                // `isFetching`, no `isLoading`: `isLoading` solo es true en la
                // carga inicial (antes de tener companyId/primeros datos). En
                // busquedas siguientes el boton nunca volvia a mostrar
                // "Buscando…" ni se deshabilitaba mientras la peticion seguia
                // en vuelo — por eso no se notaba que estaba cargando, y un
                // doble click alcanzaba a disparar dos busquedas en paralelo.
                disabled={isFetching}
                className={`${CONTROL_H} shrink-0 rounded-l-none rounded-r-[10px] bg-ink-850 px-5 text-[14px] font-semibold text-white transition-colors hover:bg-[#142d40] disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {isFetching ? 'Buscando…' : 'Buscar'}
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowMoreFilters((v) => !v)}
            data-testid="home-main-toggle-filters-button"
            aria-expanded={showMoreFilters}
            className={`${GHOST} ${CONTROL_H} justify-center py-0`}
          >
            <AdjustmentsHorizontalIcon className="h-3.5 w-3.5" />
            {showMoreFilters ? 'Ocultar filtros' : 'Más filtros'}
            <ChevronDownIcon
              className={`h-3 w-3 text-secondary-400 transition-transform ${showMoreFilters ? 'rotate-180' : ''}`}
            />
          </button>
        </div>

        {/*
          Panel EN EL FLUJO (no flotante): antes era un dropdown con
          `position: absolute` que flotaba sobre la tabla sin empujarla. A
          pedido, ahora es una seccion mas de la misma tarjeta de Filtros —
          se cierra solo con click afuera o Escape igual que antes (el
          listener esta en el useEffect de arriba), pero al abrirse la tabla
          de resultados baja como cualquier otro contenido.

          Orden de los campos (Tipo, Nombre del cliente, RUC/Cédula, Código
          de tienda, Punto de venta, Fecha Inicio, Fecha Fin, Estado) sigue
          la referencia visual pedida — sin las cajas tituladas "Documento /
          Cliente / Ubicación · Fechas" que tenia antes, todos los campos van
          en una sola fila. Los estilos (FIELD, FIELD_LABEL, chips, colores)
          son los mismos de siempre.

          `items-end` + subir a 4 columnas recien en xl (7 solo en 2xl, no en
          xl): en una laptop tipica (~1280–1440px) 7 columnas iguales dejaban
          cada campo con ~170px, muy poco para que la etiqueta ("Nombre del
          cliente", "Código de tienda") entrara en una linea — se envolvia a
          2 lineas en unos campos si y en otros no, y eso desalineaba los
          inputs entre si (cada uno arrancaba a una altura distinta). Con
          `items-end` los inputs quedan siempre al mismo nivel aunque alguna
          etiqueta se envuelva, y con menos columnas por fila en laptop hay
          espacio real para que la mayoria entre en una sola linea.
        */}
        {showMoreFilters && (
          <div className="filter-panel-in mt-4 flex flex-col gap-4 border-t border-[#f4f4f4] pt-4 dark:border-secondary-700">
            <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7">
              <div>
                <div className="mb-1 flex items-center gap-1">
                  <label htmlFor="documentType" className={FIELD_LABEL}>Tipo</label>
                  <FieldInfo text="Qué clase de documento fiscal es: factura, nota de crédito, nota de débito, y así." />
                </div>
                {/* Mismo patron que el resto de los <select> de la app (p.ej. Step1CustomerSelection): appearance-none + chevron propio, en vez de la flechita nativa del navegador que desentonaba con el resto de los campos. */}
                <div className="relative">
                  <select
                    id="documentType"
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    data-testid="home-main-filter-document-type-select"
                    className={`${FIELD} cursor-pointer appearance-none truncate pr-8`}
                  >
                    <option value="">Todos los tipos</option>
                    <option value="01">Factura de Operación Interna</option>
                    <option value="02">Factura de Importación</option>
                    <option value="03">Factura de Exportación</option>
                    <option value="04">Nota de Crédito Referente a una o Varias FE</option>
                    <option value="05">Nota de Débito Referente a una o Varias FE</option>
                    <option value="06">Nota de Crédito Genérica</option>
                    <option value="07">Nota de Débito Genérica</option>
                    <option value="08">Factura de Zona Franca</option>
                    <option value="09">Reembolso</option>
                    <option value="10">Factura de operación extranjera</option>
                  </select>
                  <svg
                    aria-hidden="true"
                    className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-secondary-400"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </div>
              </div>

              <div>
                <div className="mb-1 flex items-center gap-1">
                  <span className={FIELD_LABEL}>Nombre del cliente</span>
                </div>
                <div className="relative">
                  <UserIcon className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-secondary-400" />
                  <input
                    type="text"
                    aria-label="Nombre del Cliente"
                    placeholder="Nombre del cliente…"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    data-testid="home-main-filter-customer-name-input"
                    onKeyDown={onEnterSearch}
                    className={`${FIELD} pl-10 pr-9`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2">
                    <FieldInfo text="Busca las facturas emitidas a nombre de este cliente." />
                  </span>
                </div>
              </div>

              <div>
                <div className="mb-1 flex items-center gap-1">
                  <span className={FIELD_LABEL}>RUC / Cédula</span>
                </div>
                <div className="relative">
                  <IdentificationIcon className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-secondary-400" />
                  <input
                    type="text"
                    aria-label="RUC del Cliente"
                    placeholder="RUC / Cédula"
                    value={customerNumberId}
                    onChange={(e) => setCustomerNumberId(e.target.value)}
                    data-testid="home-main-filter-customer-number-id-input"
                    onKeyDown={onEnterSearch}
                    className={`${FIELD} pl-10 pr-9 font-mono text-[14px]`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2">
                    <FieldInfo text="Busca por el RUC o número de cédula del cliente." />
                  </span>
                </div>
              </div>

              <div>
                <div className="mb-1 flex items-center gap-1">
                  <label htmlFor="storeCode" className={FIELD_LABEL}>Código de tienda</label>
                  <FieldInfo text="El código interno de la tienda o sucursal que emitió la factura." />
                </div>
                <input
                  id="storeCode"
                  type="text"
                  placeholder="001"
                  value={storeCode}
                  onChange={(e) => setStoreCode(e.target.value)}
                  data-testid="home-main-filter-store-code-input"
                  onKeyDown={onEnterSearch}
                  className={`${FIELD} text-center font-mono`}
                />
              </div>

              <div>
                <div className="mb-1 flex items-center gap-1">
                  <label htmlFor="billingPoint" className={FIELD_LABEL}>Punto de venta</label>
                  <FieldInfo text="El número de caja o punto de venta donde se generó la factura." />
                </div>
                <input
                  id="billingPoint"
                  type="text"
                  placeholder="001"
                  value={billingPoint}
                  onChange={(e) => setBillingPoint(e.target.value)}
                  data-testid="home-main-filter-billing-point-input"
                  onKeyDown={onEnterSearch}
                  className={`${FIELD} text-center font-mono`}
                />
              </div>

              <div>
                <div className="mb-1 flex items-center gap-1">
                  <label htmlFor="initDate" className={FIELD_LABEL}>Fecha Inicio</label>
                  <FieldInfo text="Desde qué fecha de emisión buscar." />
                </div>
                <input
                  id="initDate"
                  type="date"
                  value={initDate}
                  onChange={(e) => setInitDate(e.target.value)}
                  data-testid="home-main-filter-init-date-input"
                  className={FIELD}
                />
              </div>

              <div>
                <div className="mb-1 flex items-center gap-1">
                  <label htmlFor="endDate" className={FIELD_LABEL}>Fecha Fin</label>
                  <FieldInfo text="Hasta qué fecha de emisión buscar, inclusive." />
                </div>
                <input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="home-main-filter-end-date-input"
                  className={FIELD}
                />
              </div>
            </div>

            {/*
              Estado va abajo, en su propia fila a todo el ancho: son 5
              chips y necesitan lugar para no envolver a 2 lineas (ver nota
              de tamaño de chip mas abajo).
            */}
            <div>
              <div className="mb-1 flex items-center gap-1">
                <span className={FIELD_LABEL}>Estado</span>
                <FieldInfo text="En qué situación está la factura ante la DGI. Procesada: se emitió sin problemas. Autorizado uso de la FE: todavía en trámite (pendiente, procesándose o ya autorizada, esperando emitirse). Error: algo falló al enviarla. Anulada: fue cancelada." />
              </div>
              {/*
                El diseño convierte el <select> de estado en chips. Son
                los 4 estados reales del negocio (Procesada / Autorizado
                uso de la FE / Error / Anulada — ver STATE_GROUPS), no los
                6 eInvoiceStatus granulares de Aludra: mostrar "Procesada"
                y "Procesando" como chips separados confundía porque se
                leen casi igual y no son la distinción que le importa al
                usuario. "Autorizado uso de la FE" agrupa procesando +
                pendiente + autorizado (codigos 2, 3, 4).
                Chips mas chicos que el resto de la app (px-2.5/py-1/11px)
                a proposito: son 5 opciones en una fila y el tamaño de chip
                "de tabla" (px-3/py-2/13px) las hacia envolver a 2 lineas.

                Multi-seleccion: cada chip de estado (no "Todos") se prende/
                apaga independiente de los demas — se puede filtrar por
                Procesada + Error al mismo tiempo, por ejemplo. "Todos"
                limpia la seleccion y se marca solo el solo cuando no hay
                ningun chip especifico elegido.
              */}
              <div className="inline-flex flex-wrap gap-1" data-testid="home-main-filter-invoice-status-select">
                {ESTADO_FILTER_OPTIONS.map((opt) => {
                  const active = opt.key === '' ? invoiceStatusKeys.length === 0 : invoiceStatusKeys.includes(opt.key);
                  return (
                    <button
                      key={opt.key || 'all'}
                      type="button"
                      onClick={() => {
                        if (opt.key === '') {
                          setInvoiceStatusKeys([]);
                          return;
                        }
                        setInvoiceStatusKeys((prev) =>
                          prev.includes(opt.key) ? prev.filter((k) => k !== opt.key) : [...prev, opt.key]
                        );
                      }}
                      aria-pressed={active}
                      className={
                        active
                          ? 'inline-flex items-center gap-1 rounded-md border border-ink-850 bg-ink-850 px-2.5 py-1 text-[11.5px] font-semibold text-white'
                          : 'inline-flex items-center gap-1 rounded-md border border-[#ececec] bg-white px-2.5 py-1 text-[11.5px] font-medium text-secondary-600 transition-colors hover:border-secondary-300 dark:border-secondary-600 dark:bg-secondary-800 dark:text-secondary-300'
                      }
                    >
                      {opt.dot && <span className="h-1.5 w-1.5 rounded-full" style={{ background: opt.dot }} />}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        </div>

        {/* Acciones */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[#f4f4f4] pt-3.5 dark:border-secondary-700">
          <div className="flex items-center gap-2">
            <Switch
              checked={hideErrorInvoices}
              onChange={setHideErrorInvoices}
              data-testid="home-main-hide-error-invoices-switch"
              label="Ocultar facturas con error"
              size="sm"
            />
            {hideErrorInvoices && data?.invoices && (
              <span className="text-xs text-secondary-500 dark:text-secondary-400">
                ({data.invoices.length - filteredInvoices.length} ocultas)
              </span>
            )}
          </div>
          {/*
            "Aplicar filtros" se elimino: llamaba a handleSearch, exactamente
            igual que "Buscar". Dos botones para una sola accion.
            "Limpiar filtros" solo aparece cuando hay algo que limpiar.
          */}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleClearFilters}
              data-testid="home-main-clear-filters-button"
              className="rounded px-3 py-2.5 text-[14px] font-medium text-secondary-500 underline-offset-4 transition-colors hover:text-ink-850 hover:underline dark:hover:text-white"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* Tabla */}
      <div className={`${CARD} mt-4 overflow-hidden`}>
        {/*
          Con `searchApplied` (filtro de texto/nombre/estado) no hay `total`
          exacto — "no hay facturas" solo se puede afirmar si esta es la
          primera pagina (`!cursor`), no encontro nada, Y no queda mas por
          escanear (`!data.hasMore`). Si hasMore es true, cae al bloque de
          abajo (tabla + "Siguiente") en vez de este dead-end.
        */}
        {!data || (data.searchApplied ? (data.invoices.length === 0 && !data.hasMore && !cursor) : (data.total ?? 0) === 0) ? (
          <div className="py-12 text-center">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-secondary-400" />
            <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-white">
              No hay facturas
            </h3>
            <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
              Comienza creando tu primera factura
            </p>
            <div className="mt-6">
              <Link to="/invoices/new" className={CTA} data-testid="home-main-empty-state-new-invoice-button">
                <PlusIcon className="h-4 w-4" />
                Nueva Factura
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 border-b border-[#ececec] px-5 py-4 dark:border-secondary-700">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-ink-850 dark:text-white">Resultados</span>
                  {/* Indicador de refetch en segundo plano — la tabla y el resumen
                      de arriba siguen mostrando la pagina anterior mientras llega
                      la nueva (placeholderData en useInvoices), asi que sin esto
                      un cambio de pagina no da ninguna señal de que esta cargando. */}
                  {isFetching && (
                    <span
                      aria-hidden="true"
                      className="h-3 w-3 animate-spin rounded-full border-[1.5px] border-secondary-300 border-t-[#12c4b2]"
                    />
                  )}
                </div>
                <div className="mt-0.5 text-xs text-secondary-400">
                  {data?.searchApplied
                    ? `${filteredInvoices.length} documentos en este bloque`
                    : `${filteredInvoices.length} de ${data?.total ?? 0} documentos`}
                </div>
              </div>
            </div>

            {/*
              filteredInvoices.length === 0 con data.total > 0 significa que
              "Ocultar facturas con error" ocultó TODAS las filas de esta
              página. La paginación sigue quedando abajo, fuera de este `if`,
              para que el usuario pueda avanzar de página en vez de quedar
              trabado sin ningún control visible (AgilityTask
              cmrdzshw002xpg2fiad9caumc). Con `searchApplied`, tambien puede
              significar que este bloque escaneado no tuvo coincidencias pero
              puede haber mas mas adelante (`hasMore`) — ver mensaje abajo.
            */}
            {isFetching ? (
              /*
                Búsqueda/página/filtro nuevo en vuelo: se reemplazan las filas
                por un esqueleto animado en vez de dejar la página anterior
                quieta con solo el spinner chico de "Resultados" — así se nota
                de un vistazo que se está cargando algo nuevo, sin tapar toda
                la pantalla como hace el spinner grande de la carga inicial.
              */
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] table-fixed border-collapse text-[14px]">
                  <colgroup>
                    <col className="w-[25%]" />
                    <col className="w-[23%]" />
                    <col className="w-[9%]" />
                    <col className="w-[13%]" />
                    <col className="w-[12%]" />
                    <col className="w-[13%]" />
                    <col className="w-[5%]" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className={TH}>Documento</th>
                      <th className={TH}>Cliente</th>
                      <th className={`${TH} text-center`}>Tienda · Pto</th>
                      <th className={TH}>Estado</th>
                      <th className={TH}>Monto total</th>
                      <th className={TH}>Emisión</th>
                      <th className={TH} />
                    </tr>
                  </thead>
                  <tbody>
                    <InvoiceRowsSkeleton rows={data?.pageSize ?? 10} />
                  </tbody>
                </table>
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="py-12 text-center">
                {data?.searchApplied && data?.hasMore ? (
                  // Este bloque escaneado de Aludra no tuvo coincidencias,
                  // pero el dataset no esta agotado — "Siguiente" continua el
                  // escaneo donde quedo, no reinicia desde el principio.
                  <>
                    <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-white">
                      Sin coincidencias en este bloque
                    </h3>
                    <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                      Puede haber más facturas que coincidan más adelante — probá "Siguiente" para seguir buscando.
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-white">
                      Todas las facturas de esta página tienen error
                    </h3>
                    <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                      Están ocultas por el filtro "Ocultar facturas con error". Avanza a la siguiente página o desactiva el filtro para verlas.
                    </p>
                  </>
                )}
              </div>
            ) : (
            <div className="overflow-x-auto">
              {/*
                `min-w-[900px]` en vez de `min-w-full`: con `table-fixed` las
                columnas son un % rígido del ancho de la tabla, así que sin un
                piso en px la tabla se podía angostar en pantallas chicas
                hasta volver ilegible cualquier columna (en vez de disparar el
                scroll horizontal del contenedor, que es lo que se quiere).
              */}
              <table className="w-full min-w-[900px] table-fixed border-collapse text-[14px]">
                {/*
                  `table-fixed`, no `auto`: con `auto` estos porcentajes eran
                  solo una PREFERENCIA — el navegador seguia repartiendo el
                  ancho segun el contenido de cada celda, y "Cliente" (una
                  sola linea de texto) terminaba mas ancha que "Documento"
                  (numero+tipo, CUFE, resolucion en 3 lineas), al revés de lo
                  que tiene mas contenido. Con `fixed` estos porcentajes son
                  la palabra final: Documento le gana el ancho a Cliente
                  siempre, sin importar que tan largo sea un nombre. El
                  contenido de cada celda ya viene con truncate/line-clamp
                  para no desbordar cuando el texto no entra.
                */}
                <colgroup>
                  <col className="w-[25%]" />
                  <col className="w-[23%]" />
                  <col className="w-[9%]" />
                  <col className="w-[13%]" />
                  <col className="w-[12%]" />
                  <col className="w-[13%]" />
                  <col className="w-[5%]" />
                </colgroup>
                <thead>
                  <tr>
                    <th className={TH}>Documento</th>
                    <th className={TH}>Cliente</th>
                    <th className={`${TH} text-center`}>Tienda · Pto</th>
                    <th className={TH}>Estado</th>
                    <th className={TH}>Monto total</th>
                    <th className={TH}>Emisión</th>
                    <th className={TH} />
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice) => {
                    const tag = getDocTypeTag(invoice.documentType);
                    return (
                      <tr
                        key={invoice.id}
                        className={`${ROW_HOVER} last:[&>td]:border-b-0`}
                      >
                        {/* Documento: número + tipo + CUFE + resolución */}
                        <td className={TD}>
                          <div className="inline-flex items-center gap-2">
                            <span className="font-mono font-bold text-ink-850 dark:text-white">
                              {invoice.documentNumber}
                            </span>
                            <DocTypeBadge variant={tag.variant} title={tag.title}>
                              {tag.text}
                            </DocTypeBadge>
                          </div>
                          <div className="break-all font-mono text-[10px] font-medium text-[#0d9488]">
                            {formatCufe(invoice.cufe)}
                          </div>
                          <div
                            className="mt-px line-clamp-2 break-words font-mono text-[11px] tracking-[0.06em] text-secondary-400"
                            title={invoice.resolution || undefined}
                          >
                            {invoice.resolution ? `Res. ${invoice.resolution}` : 'Sin resolución'}
                          </div>
                        </td>

                        {/* Cliente: nombre + RUC */}
                        <td className={TD}>
                          <div className="truncate font-medium text-ink-850 dark:text-white" title={invoice.customerName}>
                            {invoice.customerName}
                          </div>
                          <div className="mt-0.5 font-mono text-[12px] text-secondary-400">
                            {invoice.customerNumberId || '-'}
                          </div>
                        </td>

                        {/* Tienda · Punto de venta */}
                        <td className={`${TD} whitespace-nowrap text-center font-mono text-[13px] text-secondary-500`}>
                          <span className="font-semibold text-ink-850 dark:text-white">{invoice.storeCode || '-'}</span>
                          <span className="mx-1 text-secondary-300">·</span>
                          <span className="font-semibold text-ink-850 dark:text-white">{invoice.billingPoint || '-'}</span>
                        </td>

                        <td className={TD}>
                          <StatusBadgeWithModal
                            status={invoice.status ?? ''}
                            eInvoiceStatus={invoice.eInvoiceStatus ?? 0}
                            responseCode={invoice.responseCode}
                            resolution={invoice.resolution}
                            forceWaitOnEndpoints={invoice.forceWaitOnEndpoints}
                            testIdBase={`home-main-status-${invoice.id}`}
                          />
                        </td>

                        <td className={`${TD} whitespace-nowrap text-left font-mono text-sm font-bold text-ink-850 dark:text-white`}>
                          {CURRENCY}{' '}
                          {(invoice.amountWithTax ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>

                        <td className={`${TD} whitespace-nowrap`}>
                          <div className="font-mono font-medium text-ink-850 dark:text-white">
                            {invoice.dateIssue && format(new Date(invoice.dateIssue), 'dd/MM/yyyy')}
                          </div>
                          <div className="mt-0.5 font-mono text-[12px] text-secondary-400">
                            {/* 12h con am/pm, no 24h — 'a' de date-fns da "AM"/"PM"; se pasa a minusculas para que combine con el resto del texto, en minuscula, de la columna. */}
                            {invoice.dateIssue && format(new Date(invoice.dateIssue), 'hh:mm a').toLowerCase()}
                          </div>
                        </td>

                        <td className={`${TD} whitespace-nowrap text-center`}>
                          {/*
                            Boton de icono en vez del enlace "Ver detalles →":
                            ese texto se comia ~110px en cada fila y con 9
                            columnas la tabla terminaba scrolleando. El
                            `title` + `aria-label` mantienen la accion
                            descubrible y accesible.
                          */}
                          <Link
                            to={`/pages/invoice/${invoice.id}`}
                            state={{ from: '/home/main' }}
                            data-testid={`home-main-view-details-link-${invoice.id}`}
                            title="Ver detalles"
                            aria-label={`Ver detalles de la factura ${invoice.documentNumber ?? ''}`}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-secondary-400 transition-colors hover:border-[#ececec] hover:bg-[#f6f6f7] hover:text-ink-850 dark:hover:border-secondary-600 dark:hover:bg-secondary-700 dark:hover:text-white"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}

            {/* Paginación: se basa en el total del servidor (data.total), no en la lista
                ya filtrada por el toggle "Ocultar facturas con error" — así la paginación
                sigue siendo correcta aunque el filtro cliente oculte todas las filas de la
                página actual (ver AgilityTask cmrdzshw002xpg2fiad9caumc). Se renderiza fuera
                del condicional de filteredInvoices para que siga visible aunque el filtro
                oculte todas las filas de la página actual. */}
            {/*
              🐛 BUG real corregido: con `searchApplied`, un bloque escaneado
              con 0 coincidencias pero `hasMore: true` (Aludra tiene mas para
              escanear) dejaba el pie de pagina — Y el boton "Siguiente" —
              completamente OCULTO, porque antes solo se mostraba con
              `filteredInvoices.length > 0`. El mensaje decia "probá
              Siguiente" pero el boton no existia: un callejon sin salida
              identico a "no hay resultados" aunque Aludra sí tuviera la
              factura mas adelante en el escaneo. Con `data.hasMore` en el OR
              el pie se mantiene visible mientras haya algo mas por escanear.
            */}
            {data && (data.searchApplied ? (filteredInvoices.length > 0 || cursorHistoryRef.current.length > 0 || data.hasMore) : (data.total ?? 0) > 0) && (
              <div className="flex flex-wrap items-center justify-between gap-4 border-t border-[#ececec] bg-secondary-50 px-5 py-4 dark:border-secondary-700 dark:bg-secondary-900">
                <div className="flex items-center gap-4 text-[14px] text-secondary-500 dark:text-secondary-400">
                  {data.searchApplied ? (
                    // Sin total exacto en busquedas con filtro de texto/nombre/
                    // estado (ver InvoiceListResponse) — se pagina con
                    // Anterior/Siguiente, no con "Pagina X de Y".
                    <span>
                      <span className="font-mono font-semibold text-ink-850 dark:text-white">
                        {filteredInvoices.length}
                      </span>{' '}
                      resultado{filteredInvoices.length === 1 ? '' : 's'} en este bloque
                    </span>
                  ) : (
                    <>
                      <span>
                        Mostrando{' '}
                        <span className="font-mono font-semibold text-ink-850 dark:text-white">
                          {(page - 1) * data.pageSize + 1}–{Math.min(page * data.pageSize, data.total ?? 0)}
                        </span>{' '}
                        de <span className="font-mono font-semibold text-ink-850 dark:text-white">{data.total}</span> facturas
                      </span>
                      <span className="h-3.5 w-px bg-[#ececec] dark:bg-secondary-700" />
                      <span>
                        Página <span className="font-mono font-semibold text-ink-850 dark:text-white">{page}</span> de{' '}
                        <span className="font-mono">{totalPages}</span>
                      </span>
                    </>
                  )}
                </div>
                <div className="inline-flex items-center gap-1.5">
                  <button
                    type="button"
                    disabled={(data.searchApplied ? cursorHistoryRef.current.length === 0 : page === 1) || isFetching}
                    onClick={handlePrevPage}
                    data-testid="home-main-pagination-prev-button"
                    className="h-8 min-w-[32px] rounded-lg border border-[#ececec] bg-white px-3 text-[14px] font-medium text-secondary-500 transition-colors hover:border-secondary-300 hover:text-ink-850 disabled:cursor-not-allowed disabled:opacity-40 dark:border-secondary-600 dark:bg-secondary-800"
                  >
                    Anterior
                  </button>
                  {!data.searchApplied && (
                    <span className="inline-flex h-8 min-w-[32px] items-center justify-center rounded-lg border border-ink-850 bg-ink-850 px-2 text-[14px] font-medium text-white">
                      {page}
                    </span>
                  )}
                  <button
                    type="button"
                    disabled={(data.searchApplied ? !data.hasMore : page * data.pageSize >= (data.total ?? 0)) || isFetching}
                    onClick={handleNextPage}
                    data-testid="home-main-pagination-next-button"
                    className="h-8 min-w-[32px] rounded-lg border border-[#ececec] bg-white px-3 text-[14px] font-medium text-secondary-500 transition-colors hover:border-secondary-300 hover:text-ink-850 disabled:cursor-not-allowed disabled:opacity-40 dark:border-secondary-600 dark:bg-secondary-800"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
